<script lang="ts">
  import { 
    differenceInDays, 
    format, 
    startOfDay, 
    subDays, 
    addDays, 
    isToday
  } from "date-fns";
  import { format as formatTz, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
  import { getAppContext } from "../AppContext";
  import { today } from "@cohesionib/biblioteca/lib/util/dates";
  import type { 
    GetOccupancyCurrentQuery, 
    GetOccupantTypeCountByBucketQuery 
  } from "../frontendGatewaySdk";
  import { TabPage, SubPage } from "@cohesionib/biblioteca/lib/TabPage";
  import Calendar32 from "carbon-icons-svelte/lib/Calendar32";
  import PageBox from "../shared/PageBox.svelte";
  import OccupancySensor from "./OccupancySensor/OccupancySensor.svelte";
  import DateRangeModal from "../shared/DateRangeModal.svelte";
  import OccupancyKeycard from "./OccupancyKeycard/OccupancyKeycard.svelte";
  import enUS from "date-fns/locale/en-US";

  export let occupancyCurrent: GetOccupancyCurrentQuery | null = null;

  const { feGatewaySdk, buildingCode, buildingCodeDemoList, buildingButlrSensorList } = getAppContext();

  let startDate: Date = today;
  let endDate: Date = today;
  let occupantTypeCountByBucketData: GetOccupantTypeCountByBucketQuery | null = null;
  let error: string | null = null;
  let isLoading: boolean = false;
  let includeWeekends: boolean = false;
  let previousOccupancyValues: GetOccupantTypeCountByBucketQuery | null = null;
  let dateTitle = format(
      utcToZonedTime(new Date(), "America/Chicago"), 
        "MMMM d', 'yyyy' | As of 'h:mmbbbb"
    )
    .replaceAll(".", "")
    .replace("noon", "pm");
  let dateTitleTimeZone = formatTz(
      utcToZonedTime(new Date(), "America/Chicago"), 
      "zzz", {
        timeZone: "America/Chicago",
        locale: enUS,
    });
   
  $: response = occupancyCurrent?.building || null;
  $: capacity = response.capacity;
  $: occupancyBySensor = response?.occupancyBySensor || [];
  $: pickerStartDate = startOfDay(startDate);
  $: pickerEndDate = startOfDay(addDays(endDate, 1));
  $: rangeDays = differenceInDays(pickerEndDate, pickerStartDate);

  $: occupantTypeCountByBucketPromise = feGatewaySdk
          .getOccupantTypeCountByBucket({
            buildingCode: buildingCodeDemoList.includes(buildingCode.toLowerCase()) ? "wwsf1" : buildingCode.toLowerCase(),
            fromDate: zonedTimeToUtc(pickerStartDate, 'America/Chicago'),
            toDate: zonedTimeToUtc(pickerEndDate, 'America/Chicago'),
            includeWeekends: rangeDays === 1 ? true : includeWeekends
          })
          .then((d: any) => {
            error = null;
            occupantTypeCountByBucketData = d;
            isLoading = false;
          })
          .catch((e: string) => {
            error = 'An error occurred. Please try again using another selected date(s)';
            isLoading = false;
          });

  const getPreviousOccupancyValues = (start, end) => {
    feGatewaySdk
            .getOccupantTypeCountByBucket({
              buildingCode: buildingCodeDemoList.includes(buildingCode.toLowerCase()) ? "wwsf1" : buildingCode.toLowerCase(),
              fromDate: zonedTimeToUtc(subDays(start, 7), 'America/Chicago'),
              toDate: zonedTimeToUtc(subDays(end, 7), 'America/Chicago'),
              includeWeekends: rangeDays === 1 ? true : includeWeekends
            })
            .then((d: any) => {
              error = null;
              previousOccupancyValues = d;
              isLoading = false;
              return d
            })
  }

  // In the case we have only one day selected, we want to compare the occupancy between the day selected and the same day a week before.
  $: previousOccupancePromise = rangeDays ===  1 ? getPreviousOccupancyValues(pickerStartDate, pickerEndDate) : null;
</script>

<style>
  div {
    font-weight: 300;
  }
  #date-title-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 1em;
  }
  .date-title {
    color: #141414;
    font-weight: 600;
    display: flex;
  }
  .date-title-light {
    font-weight: 300;
    color: #717171;
    font-size: 1.25em;
  }
</style>

<PageBox>
  <div id="date-title-container">
    <div class="date-title">
      <Calendar32/>
      <DateRangeModal bind:startDate bind:endDate bind:isLoading bind:error bind:includeWeekends/>
    </div>
    {#if isToday(endDate)}
      <span class="date-title-light">{dateTitle.split('|')[1]} {dateTitleTimeZone}</span>
    {/if}
  </div>
  <TabPage showDropShadow={false} justifyRight={false}>
    {#if buildingCode.toLowerCase() !== 'cib'}
      <SubPage id="keycards" title="Keycards">
        <OccupancyKeycard
          bind:occupantTypeCountByBucketData
          includeWeekends={rangeDays === 1 ? true : includeWeekends}
          bind:capacity
          bind:rangeDays
          bind:previousOccupancyValues
          bind:pickerStartDate
          bind:pickerEndDate
        />
      </SubPage>
    {/if}
    {#if occupancyBySensor.length || buildingCodeDemoList.includes(buildingCode.toLowerCase()) || Object.keys(buildingButlrSensorList).includes(buildingCode.toLowerCase())}
      <SubPage id="occupancy-sensors" title="Sensor">
        <OccupancySensor />
      </SubPage>
    {/if}
  </TabPage>
</PageBox>
